import { useEffect, useRef, useState } from 'react';
import {
	addDays,
	addMonths,
	addWeeks,
	differenceInMonths,
	endOfWeek,
	format,
	formatISO,
	setDate,
	getDay,
	getDaysInMonth,
	isAfter,
	isSameDay,
	isWithinInterval,
	max,
	min,
	parseISO,
	startOfWeek,
	subDays,
	subWeeks,
	subMonths,
} from 'date-fns/fp';
import castArray from 'lodash/fp/castArray';
import concat from 'lodash/fp/concat';
import flatten from 'lodash/fp/flatten';
import flow from 'lodash/fp/flow';
import get from 'lodash/fp/get';
import keyBy from 'lodash/fp/keyBy';
import map from 'lodash/fp/map';
import mapValues from 'lodash/fp/mapValues';
import split from 'lodash/fp/split';
import update from 'lodash/fp/update';

import { getRTL } from '@micro-apps/env';
import Carousel from '../Carousel/Carousel';
import Month from './Month';
import './CalendarOld.scss';

const mapWithIndex = (map as any).convert({ cap: false });
const increment = n => n + 1;
const setDateForMonth = month => date => setDate(date)(month);

type Availability = {
	date: string;
	status: string;
};

interface Range {
	start?: Date | null;
	end?: Date | null;
}

interface AvailableRange {
	start: Date;
	end: Date;
}

function getDaysForMonth({
	month,
	availableRange,
	availabilityByDate = {},
	selectedRange,
	focusedDate,
	onClick,
}: {
	month: Date;
	availableRange: AvailableRange;
	availabilityByDate: { [date: string]: string };
	selectedRange: Range;
	focusedDate: Date;
	onClick: (date: Date) => void;
}) {
	const emptyDays = flow(
		getDay,
		firstDay => [...Array(firstDay).keys()],
		map(() => null)
	)(month);

	return flow(
		getDaysInMonth,
		numberOfDays => [...Array(numberOfDays).keys()],
		map(increment),
		map(setDateForMonth(month)),
		map(date => {
			const availabilityStatus = availabilityByDate[format('yyyy-MM-dd')(date)] || '1';
			const isUnavailable = availabilityStatus === '0';
			const isInvalidOrOutsideRange = availabilityStatus === '.';
			return {
				date,
				tabIndex: isSameDay(focusedDate)(date) ? 0 : -1,
				ariaLabel: format('PPPP')(date),
				isSelected:
					selectedRange?.start &&
					isWithinInterval({
						start: selectedRange.start,
						end: selectedRange.end || selectedRange.start,
					})(date),
				isSelectedStart: selectedRange?.start && isSameDay(selectedRange.start)(date),
				isSelectedEnd: selectedRange?.end && isSameDay(selectedRange.end)(date),
				isRestricted: false,
				isUnavailable,
				disabled: isInvalidOrOutsideRange || !isWithinInterval(availableRange)(date),
				onClick: () => onClick(date),
			};
		}),
		concat(emptyDays)
	)(month);
}

function getMonths({ selectedRange, availableRange, availabilityByDate, focusedDate, onDayClick }) {
	return [...Array(differenceInMonths(availableRange.start)(availableRange.end) + 1).keys()]
		.map(monthNumber => flow(setDate(1), addMonths(monthNumber))(availableRange.start))
		.map(month => {
			const days = getDaysForMonth({
				month,
				availableRange,
				availabilityByDate,
				selectedRange,
				focusedDate,
				onClick: onDayClick,
			});

			return {
				month,
				days,
			};
		});
}

type Props = {
	isDesktop: boolean;
	isRange?: boolean;
	firstDayOfWeek?: number;
	selectedRange: Range;
	availableRange: AvailableRange;
	availability?: Availability[];
	onRangeChange: (range: Range) => void;
	onClose?: () => void;
};

export default function Calendar({
	isDesktop,
	isRange = true,
	firstDayOfWeek = 0,
	selectedRange,
	availableRange,
	availability = [],
	onRangeChange,
	onClose,
}: Props) {
	const dayPicker = useRef<HTMLDivElement>(null);

	const [isFirstClick, setIsFirstClick] = useState(true);
	const [focusedDate, setFocusedDate] = useState(selectedRange.start || availableRange.start);

	const handleDayClick = date => {
		if (
			isRange &&
			!isFirstClick &&
			selectedRange?.start &&
			isAfter(selectedRange.start)(date) &&
			(!selectedRange?.end || isSameDay(selectedRange.end)(selectedRange.start))
		) {
			onRangeChange({ end: date });
		} else {
			onRangeChange({ start: date, end: date });
		}
		setIsFirstClick(false);
	};

	const availabilityByDate = flow(
		map(update('status', split(''))),
		map(({ date, status }) => {
			return mapWithIndex((value, index) => {
				return {
					date: flow(parseISO, addDays(index), format('yyyy-MM-dd'))(date),
					status: value,
				};
			})(status);
		}),
		flatten,
		keyBy('date'),
		mapValues(get('status'))
	)(availability);

	const keepDateWithinRange = fn =>
		flow(
			fn,
			castArray,
			concat(availableRange.start),
			max,
			castArray,
			concat(availableRange.end),
			min
		);

	const months = getMonths({
		selectedRange,
		availableRange,
		availabilityByDate,
		focusedDate,
		onDayClick: handleDayClick,
	});

	function handleKeyDown(evt) {
		const isRTL = getRTL();

		switch (evt.key) {
			case 'ArrowUp':
				evt.preventDefault();
				evt.stopPropagation();
				setFocusedDate(keepDateWithinRange(subWeeks(1)));
				break;

			case 'ArrowLeft':
				evt.preventDefault();
				evt.stopPropagation();
				setFocusedDate(keepDateWithinRange(isRTL ? addDays(1) : subDays(1)));
				break;

			case 'Home':
				evt.preventDefault();
				evt.stopPropagation();
				setFocusedDate(keepDateWithinRange(startOfWeek));
				break;

			case 'PageUp':
				evt.preventDefault();
				evt.stopPropagation();
				setFocusedDate(keepDateWithinRange(subMonths(1)));
				break;

			case 'ArrowDown':
				evt.preventDefault();
				evt.stopPropagation();
				setFocusedDate(keepDateWithinRange(addWeeks(1)));
				break;

			case 'ArrowRight':
				evt.preventDefault();
				evt.stopPropagation();
				setFocusedDate(keepDateWithinRange(isRTL ? subDays(1) : addDays(1)));
				break;

			case 'End':
				evt.preventDefault();
				evt.stopPropagation();
				setFocusedDate(keepDateWithinRange(endOfWeek));
				break;

			case 'PageDown':
				evt.preventDefault();
				evt.stopPropagation();
				setFocusedDate(keepDateWithinRange(addMonths(1)));
				break;

			case 'Escape':
				evt.preventDefault();
				evt.stopPropagation();
				onClose?.();
				break;

			default:
				return;
		}
	}

	const focusedDateStr = format('yyyy-MM-dd')(focusedDate);

	useEffect(() => {
		const btn = dayPicker?.current?.querySelector(
			`.CalendarDay_button_${focusedDateStr}`
		) as HTMLButtonElement;
		btn.focus();
	}, [focusedDateStr]);

	return (
		<div onKeyDown={handleKeyDown} ref={dayPicker} className="Calendar">
			<Carousel
				id="calendar-carousel"
				showArrows={isDesktop}
				elements={months.map(({ month, days }) => (
					<Month
						key={formatISO(month)}
						firstDayOfWeek={firstDayOfWeek}
						title={format('MMMM yyyy')(month)}
						days={days}
						renderWeekHeader={isDesktop}
					/>
				))}
			/>
		</div>
	);
}
